/* eslint-disable react/destructuring-assignment */
import React, { useState } from 'react';
import { Button, Checkbox, Form, Input, message, Modal, Switch } from 'antd';
import { Info, Store, TriangleAlert } from 'lucide-react';

import { useCreateProductFromPlanogram } from '@/store/Product/useCreatePlanogramFromProduct';
import { useTheme } from 'styled-components';
import * as S from './styles';
import { FormPlanogram } from './_components/FormPlanogram';
import { PlanogramReview } from './_components/PlanogramReview';
import { useOverwriteStores } from './_components/PlanogramReview/useOverwriteStores';

type ModalAddPlanogramFromProductProps = {
    open: boolean;
    onOk: () => void;
    onCancel: () => void;
};

export const ModalAddPlanogramFromProduct = (
    props: ModalAddPlanogramFromProductProps,
) => {
    const form = Form.useFormInstance();

    const { colors, theme } = useTheme();

    const [showApplyConfirm, setShowApplyConfirm] = useState(false);

    const stepForm = Form.useWatch('step', form);
    const storeIds = Form.useWatch(['stores'], form);
    const isEditing = Form.useWatch('isEditing', form);
    const planogram = Form.useWatch(['planogram'], form);

    const acceptedTerms = Form.useWatch('confirmOverwrite', form);

    const { storesToOverwrite } = useOverwriteStores();

    const {
        mutateAsync: createProductFromPlanogram,
        isLoading: isLoadingCreatePlanogram,
    } = useCreateProductFromPlanogram();

    const values = Form.useWatch(['planogram'], form);

    const isFormComplete =
        !!values?.product_id &&
        !!values?.planogram_code &&
        !!values?.status &&
        values?.stock_amount !== null &&
        values?.alert_level !== null &&
        values?.price_list_in_cents > 0 &&
        values?.price_final_in_cents > 0 &&
        values?.standard_amount !== null;

    const savePlanogram = async () => {
        try {
            await createProductFromPlanogram({
                storeIds,
                alert_level: planogram.alert_level,
                planogram_code: planogram.planogram_code,
                price_final_in_cents: planogram.price_final_in_cents,
                price_list_in_cents: planogram.price_list_in_cents,
                product_id: Number(planogram.product_id),
                standard_amount: planogram.standard_amount,
                status: planogram.status,
                stock_amount: planogram.stock_amount,
            });
            props.onOk();
            /** Reset fields */
            form.resetFields([
                'stores',
                'confirmOverwrite',
                'skipAccept',
                'planogramModified',
                'step',
            ]);

            form.setFieldsValue({
                planogram: {
                    alert_level: null,
                    planogram_code: '',
                    price_final_in_cents: null,
                    price_list_in_cents: null,
                    standard_amount: null,
                    status: undefined,
                    stock_amount: null,
                    product_id: isEditing ? planogram.product_id : null,
                },
            });

            form.setFieldValue('successCreatePlanogram', true);
            message.success('Produto adicionado aos planogramas com sucesso!');
        } catch (error) {
            message.error(
                'Erro ao adicionar produto aos planogramas. Tente novamente.',
            );
            console.error('Error creating product from planogram:', error);
        }
    };

    const validateForm = async () => {
        try {
            if (stepForm === 1) {
                await form.validateFields();
                form.setFieldValue('step', 2);
            }

            if (stepForm === 2) {
                setShowApplyConfirm(true);
            }
        } catch (errorInfo) {
            form.submit();
        }
    };

    const prevStep = () => {
        if (stepForm === 1) return;

        form.setFieldValue('step', 1);
    };

    const handleCancelApplyConfirm = () => {
        setShowApplyConfirm(false);
    };

    const handleConfirmApply = async () => {
        await savePlanogram();
        setShowApplyConfirm(false);
    };

    const disableButton =
        !isFormComplete || (stepForm === 2 && !storeIds?.length);

    const disableConfirmApply = storesToOverwrite.length > 0 && !acceptedTerms;

    const renderFooter = () => {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '8px',
                    background:
                        theme === 'dark' ? colors.colorBgElevated : '#fff',
                }}
            >
                {stepForm === 2 && <Button onClick={prevStep}>Voltar</Button>}
                <Button
                    type="primary"
                    onClick={validateForm}
                    disabled={disableButton || isLoadingCreatePlanogram}
                    loading={isLoadingCreatePlanogram}
                >
                    Continuar
                </Button>
            </div>
        );
    };

    return (
        <>
            <S.StyledModal
                open={props.open}
                centered
                onOk={props.onOk}
                onCancel={props.onCancel}
                styles={{ body: { height: '80vh', padding: '0px 0px' } }}
                style={{ minWidth: '80vw' }}
                footer={renderFooter()}
                destroyOnHidden
            >
                <Form.Item
                    name="successCreatePlanogram"
                    hidden
                    initialValue={false}
                    noStyle
                    valuePropName="checked"
                >
                    <Switch />
                </Form.Item>
                <S.ContentTitle>
                    <S.TextTitle>
                        Adicionar produto em vários planogramas
                    </S.TextTitle>
                </S.ContentTitle>

                <S.StepContent hidden={stepForm !== 1}>
                    <FormPlanogram />
                </S.StepContent>

                <S.StepContent hidden={stepForm !== 2}>
                    <PlanogramReview />
                </S.StepContent>

                <Form.Item name="step" hidden>
                    <Input />
                </Form.Item>
            </S.StyledModal>

            <Modal
                title={
                    <S.ConfirmTitle>
                        <TriangleAlert color="#FAAD14" size={22} />
                        Atenção
                    </S.ConfirmTitle>
                }
                open={showApplyConfirm}
                onCancel={handleCancelApplyConfirm}
                onOk={handleConfirmApply}
                okText="Aplicar"
                cancelText="Cancelar"
                width={600}
                destroyOnHidden
                closable={!isLoadingCreatePlanogram}
                mask={{ closable: !isLoadingCreatePlanogram }}
                keyboard={!isLoadingCreatePlanogram}
                cancelButtonProps={{ disabled: isLoadingCreatePlanogram }}
                okButtonProps={{
                    loading: isLoadingCreatePlanogram,
                    disabled: disableConfirmApply,
                }}
            >
                <p>
                    Deseja adicionar o produto &quot;{planogram?.name}&quot; aos
                    planogramas? Essa ação não poderá ser desfeita.
                </p>

                {storesToOverwrite.length > 0 && (
                    <>
                        <S.OverwriteWarning>
                            <S.OverwriteWarningHeader>
                                <Info size={20} />
                                <span>
                                    Algumas lojas selecionadas já possuem este
                                    produto no planograma
                                </span>
                            </S.OverwriteWarningHeader>
                            <p>
                                Ao continuar, os dados do planograma serão
                                sobrescritos nas seguintes lojas:
                            </p>
                            <S.OverwriteList>
                                {storesToOverwrite.map((store) => (
                                    <S.OverwriteListItem key={store.id}>
                                        <Store size={16} />
                                        <span>{store.name}</span>
                                    </S.OverwriteListItem>
                                ))}
                            </S.OverwriteList>
                        </S.OverwriteWarning>
                        <S.ConfirmOverwriteRow>
                            <Form.Item
                                name="confirmOverwrite"
                                valuePropName="checked"
                                noStyle
                            >
                                <Checkbox />
                            </Form.Item>
                            <S.ConfirmOverwriteLabel>
                                Entendi que os valores serão sobrescritos
                            </S.ConfirmOverwriteLabel>
                        </S.ConfirmOverwriteRow>
                    </>
                )}
            </Modal>
        </>
    );
};
